/* eslint-disable no-console */
import './utils/polyfills/array-includes';
import './utils/polyfills/object-keys';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { SkeletonTheme } from 'react-loading-skeleton';
import { Provider } from 'react-redux';
import { StatsigProvider } from 'statsig-react';
import registerServiceWorker from './registerServiceWorker';
import { BUILD } from './utils/config';
import { addGTMBody, addGTMHead, clervertapNotificationAfterTimeout } from './utils/library';
import { STATSIG_ENVIRONMENTS } from './utils/constants';
import { store } from './store';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import 'semantic-ui-css/semantic.min.css';
import 'react-datepicker/dist/react-datepicker.css';
import './utils/styles/main.css';

import App from './components/app/App';
import Theme from './Theme';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import COLORS from './common/styles/color-pallete';
import ChatBotWrapper from './screens/help-center/wrappers/ChatBotWrapper';
import { statsigIdentifer } from './constants/evs/statsigClientKey';
import SEOMetaTags from './components/seo/SEOMetaTags';

const statsigOptions = () => {
  return {
    environment: STATSIG_ENVIRONMENTS.PRODUCTION
  };
};

function main(Component) {
  const container = document.getElementById('app');
  const root = createRoot(container);
  root.render(
    <StatsigProvider
      sdkKey={statsigIdentifer}
      waitForInitialization={true}
      options={statsigOptions}
    >
      <ThemeProvider theme={Theme}>
        <BrowserRouter >
          <Provider store={store}>
            <ErrorBoundary>
              <SkeletonTheme baseColor={COLORS.black} highlightColor={COLORS.grey200}>
                <ChatBotWrapper />
                <SEOMetaTags />
                <Component />
              </SkeletonTheme>
            </ErrorBoundary>
          </Provider>
        </BrowserRouter>
      </ThemeProvider>
    </StatsigProvider>
  );

  addGTMHead();
  addGTMBody();
  window.dataLayer = window.dataLayer || [];

  // Experimental for traking in clavertab based on environment
  window.dataLayer.buildType = BUILD;
}

if (module.hot) {
  module.hot.accept('./components/app/App', () => {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const NextApp = require('./components/app/App').default;
    main(NextApp);
  });
}

async function _main(Component) {
  //web push notification using clevertap
  BUILD === 'prod' && window.clevertap && clervertapNotificationAfterTimeout();

  //GTM INITIALIZATION
  addGTMHead();
  addGTMBody();
  window.dataLayer = window.dataLayer || [];
  // Experimental for traking in clavertab based on environment
  window.dataLayer.buildType = BUILD;

  await new Promise((resolve) =>
    // eslint-disable-next-line react/no-render-return-value
    createRoot(document.getElementById('app')).render(
      <StatsigProvider
        sdkKey={statsigIdentifer}
        waitForInitialization={true}
        options={statsigOptions}
      >
        <ThemeProvider theme={Theme}>
          <BrowserRouter >
            <Provider store={store}>
              <ErrorBoundary>
                <SkeletonTheme baseColor={COLORS.black} highlightColor={COLORS.grey200}>
                  <ChatBotWrapper />
                  <SEOMetaTags />
                  <Component />
                </SkeletonTheme>
              </ErrorBoundary>
            </Provider>
          </BrowserRouter>
        </ThemeProvider>
      </StatsigProvider>,
      resolve
    )
  );

  //register service worker
  BUILD === 'prod' && (await registerServiceWorker());
  //all done
  return 'DONE_RENDERING';
}

if (BUILD === 'prod') {
  _main(App).then(console.log).catch(console.error);
} else {
  main(App);
}
